import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSubscriptionStore } from '@/core/store/useSubscriptionStore';
import { BILLING_CREDITS, DEFAULT_REQUIRED_CREDITS } from '@/config/billingCredits';
import { maybeShowReminder } from '@/core/billing/billingNoticeManager';

const LOW_CREDITS_THRESHOLD = 5;
const ONE_OFF_EXPIRY_DAYS = 3;
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

const dispatchBillingNotice = (detail) => {
    if (typeof window === 'undefined') {
        return;
    }
    try {
        console.info('[billing] dispatch notice', { detail });
        window.dispatchEvent(new CustomEvent('billing:notice', { detail }));
    } catch (error) {
        console.warn('[billing] Failed to dispatch notice:', error);
    }
};

const resolveRequiredCredits = (actionKey) => {
    if (!actionKey) {
        return DEFAULT_REQUIRED_CREDITS;
    }
    return BILLING_CREDITS[actionKey] ?? DEFAULT_REQUIRED_CREDITS;
};

const getCreditsTotal = (credits) => {
    const total = credits?.total;
    return Number.isFinite(total) ? total : 0;
};

export function useBillingGuard() {
    const subscriptionStore = useSubscriptionStore();
    const { t } = useI18n();

    const creditsTotal = computed(() => getCreditsTotal(subscriptionStore.credits));

    const checkPostLogin = async () => {
        console.info('[billing] post-login check start');
        try {
            await subscriptionStore.syncStatus();
        } catch (error) {
            console.warn('[billing] status sync failed:', error);
            return;
        }

        const entitlement = subscriptionStore.entitlement;
        const credits = subscriptionStore.credits;
        const now = Date.now();

        console.info('[billing] post-login status', {
            entitlementKind: entitlement?.kind || null,
            expiresAt: entitlement?.expires_at || null,
            creditsTotal: getCreditsTotal(credits)
        });

        let noticeShown = false;

        if (entitlement?.kind === 'one_off' && entitlement?.expires_at) {
            const expiryTime = new Date(entitlement.expires_at).getTime();
            if (Number.isFinite(expiryTime)) {
                const remainingMs = expiryTime - now;
                if (remainingMs <= ONE_OFF_EXPIRY_DAYS * ONE_DAY_MS && remainingMs > 0) {
                    console.info('[billing] one-off expiring notice', {
                        expiresAt: entitlement.expires_at,
                        remainingMs
                    });
                    dispatchBillingNotice({
                        // 0.9.6.7：此前三处都不传 reasonCode ⇒ 弹窗类型在埋点里恒为兜底值
                        // permission_blocked（accountBillingEvents 早已声明这三个枚举），
                        // 事后完全分不清用户看到的是哪一种弹窗。
                        reasonCode: 'one_off_expiring',
                        title: t('billing.notices.oneOffExpiring.title'),
                        message: t('billing.notices.oneOffExpiring.message'),
                        primaryLabel: t('billing.notices.oneOffExpiring.primaryLabel'),
                        secondaryLabel: t('billing.notices.oneOffExpiring.secondaryLabel'),
                        redirect: '/dashboard'
                    });
                    noticeShown = true;
                }
            }
        }

        if (!noticeShown && getCreditsTotal(credits) < LOW_CREDITS_THRESHOLD) {
            console.info('[billing] low credits notice', {
                creditsTotal: getCreditsTotal(credits),
                threshold: LOW_CREDITS_THRESHOLD
            });
            dispatchBillingNotice({
                reasonCode: 'low_credits',
                title: t('billing.notices.lowCredits.title'),
                message: t('billing.notices.lowCredits.message'),
                primaryLabel: t('billing.notices.lowCredits.primaryLabel'),
                secondaryLabel: t('billing.notices.lowCredits.secondaryLabel'),
                redirect: '/dashboard'
            });
            noticeShown = true;
        }

        // v2.1.13：若未弹其他 notice，评估未消费提醒（首次发放由 store watcher 独立处理，不在此处触发）。
        // 实践中首次发放用户刚拿到积分，不会同时命中 oneOffExpiring/lowCredits，二者互斥，无需额外互斥逻辑。
        if (!noticeShown) {
            maybeShowReminder();
        }
    };

    const checkCreditsForAction = async (actionKey) => {
        const requiredCredits = resolveRequiredCredits(actionKey);
        let totalCredits = creditsTotal.value;

        if (!Number.isFinite(subscriptionStore.credits?.total)) {
            console.info('[billing] credits missing, syncing status', {
                actionKey,
                lastSyncedAt: subscriptionStore.lastSyncedAt,
                credits: subscriptionStore.credits
            });
            try {
                await subscriptionStore.syncStatus();
            } catch (error) {
                console.warn('[billing] status sync failed while checking credits:', error);
            }
            totalCredits = getCreditsTotal(subscriptionStore.credits);
        }

        console.info('[billing] credits check', {
            actionKey,
            requiredCredits,
            totalCredits
        });

        if (requiredCredits <= 0) {
            return { allowed: true, requiredCredits, totalCredits };
        }

        if (requiredCredits <= totalCredits) {
            return { allowed: true, requiredCredits, totalCredits };
        }

        console.info('[billing] insufficient credits', {
            actionKey,
            requiredCredits,
            totalCredits
        });
        dispatchBillingNotice({
            reasonCode: 'insufficient_credits',
            title: t('billing.notices.insufficientCredits.title'),
            message: t('billing.notices.insufficientCredits.message', {
                requiredCredits,
                totalCredits
            }),
            primaryLabel: t('billing.notices.insufficientCredits.primaryLabel'),
            secondaryLabel: t('billing.notices.insufficientCredits.secondaryLabel'),
            redirect: '/dashboard'
        });

        return { allowed: false, requiredCredits, totalCredits };
    };

    return {
        creditsTotal,
        checkPostLogin,
        checkCreditsForAction
    };
}
